import { motion } from "framer-motion";
import { Search, MessageCircle, Mail, Phone, FileText, Book, HelpCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { useState } from "react";

const faqs = [
  {
    question: "How do I get started with PromptPerfect?",
    answer: "Getting started is easy! Simply sign up for an account, choose your plan, and you can immediately begin refining your prompts. We offer a comprehensive tutorial for new users to help you understand all features."
  },
  {
    question: "What types of prompts can I optimize?",
    answer: "PromptPerfect supports various types of prompts including those for image generation (Midjourney, DALL-E), text generation (GPT models), code generation, and more. Our system adapts to different AI models and use cases."
  },
  {
    question: "Do you offer enterprise solutions?",
    answer: "Yes, we provide enterprise solutions with custom features, dedicated support, and SLA guarantees. Contact our sales team for more information about enterprise plans and custom integrations."
  }
];

const helpCategories = [
  {
    icon: <Book className="w-6 h-6" />,
    title: "Documentation",
    description: "Comprehensive guides and API documentation",
    link: "/docs"
  },
  {
    icon: <FileText className="w-6 h-6" />,
    title: "Tutorials",
    description: "Step-by-step tutorials and best practices",
    link: "/tutorials"
  },
  {
    icon: <MessageCircle className="w-6 h-6" />,
    title: "Community",
    description: "Join our community forum for discussions",
    link: "/community"
  }
];

const Support = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-background">
      <Header 
        title="Support Center"
        description="Get help with PromptPerfect. Find answers, tutorials, and connect with our support team."
      />

      <div className="container mx-auto px-6 py-12">
        {/* Search Section */}
        <div className="max-w-2xl mx-auto mb-16">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <input
              type="text"
              placeholder="Search for help..."
              className="w-full pl-12 pr-4 py-3 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent text-lg"
            />
          </div>
        </div>

        {/* Help Categories */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {helpCategories.map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                {category.icon}
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">{category.title}</h3>
              <p className="text-muted-foreground mb-4">{category.description}</p>
              <Button variant="outline" className="w-full">
                View {category.title}
              </Button>
            </motion.div>
          ))}
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="border border-border rounded-lg overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 text-left bg-card hover:bg-accent flex justify-between items-center"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <span className="font-medium text-foreground">{faq.question}</span>
                  <ChevronDown
                    className={`w-5 h-5 transform transition-transform duration-200 ${
                      expandedFaq === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 py-4 bg-muted">
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Contact Options */}
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-foreground mb-8">Still Need Help?</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-card p-8 rounded-xl shadow-lg">
              <Mail className="w-8 h-8 mx-auto mb-4 text-primary" />
              <h3 className="text-xl font-semibold text-foreground mb-2">Email Support</h3>
              <p className="text-muted-foreground mb-4">Get help from our support team within 24 hours</p>
              <Button>
                Send Email
                <Mail className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="bg-card p-8 rounded-xl shadow-lg">
              <MessageCircle className="w-8 h-8 mx-auto mb-4 text-primary" />
              <h3 className="text-xl font-semibold text-foreground mb-2">Live Chat</h3>
              <p className="text-muted-foreground mb-4">Chat with our support team in real-time</p>
              <Button>
                Start Chat
                <MessageCircle className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Support; 